import { XAPI_CONFIG } from "@config/xapi-config";
import { getDeviceInfo, parseJwt } from "@lib/browser-utils";

/**
 * xAPITrackingService provides a single point of entry for xAPI tracking needs.
 * It manages xAPI tracking for learning analytics.
 */
class xAPITrackingService {
  #apiEndpoint = "";
  #enabled = false;
  #userId = "";
  #sessionId = "";
  #organizationId = "";
  #userRoleId = "";
  #classroomId = "";
  #productId = "";
  #token = "";
  #initialized = false;
  #isSsoLogin = false;
  /**
   * Creates a new xAPITrackingService instance.
   */
  constructor() {
    this.#enabled = XAPI_CONFIG.ENABLED;
    this.#apiEndpoint = XAPI_CONFIG.API_ENDPOINT;
    if (this.#enabled && !this.#apiEndpoint) {
      this.#enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
    }
  }

  /**
   * Configures the xAPITrackingService with user and context information.
   * @param {Object} currentUser - Current user information
   */
  configure(currentUser) {
    if (!currentUser) {
      this.#initialized = false;
      return;
    }

    this.setUserId(currentUser.userId);
    this.setOrganizationId(currentUser.organizationId);
    this.setUserRoleId(currentUser);
    this.setSessionId(currentUser.sessionId);
    this.setToken(currentUser.token);
    this.setIsSsoLoginFlag(currentUser.isSso);
    // this.setXApiEventService(xApiEventService);
    this.#initialized = true;
  }

  /**
   * Sets the user id for the current session.
   * @param {string} userId - New user id
   */
  setUserId(userId) {
    this.#userId = userId;
  }

  /**
   * Sets the organization id for the current session.
   * @param {string} organizationId - Organization id
   */
  setOrganizationId(organizationId) {
    this.#organizationId = organizationId.toString();
  }

  /**
   * Sets the user role id for the current session.
   * @param {string} currentUser - Current user
   */
  setUserRoleId(currentUser) {
    const token = parseJwt(currentUser.token);
    const userRoleId = token?.act;
    this.#userRoleId = userRoleId.toString();
  }

  /**
   * Sets the session id for the current session.
   * @param {string} sessionId - New session id
   */
  setSessionId(sessionId) {
    this.#sessionId = sessionId;
  }

  /**
   * Sets the authentication token for the current session.
   * @param {string} token - Authentication token
   */
  setToken(token) {
    this.#token = token || "";
  }

  /**
   * Sets the isSsoLogin flag.
   * @param {boolean} isSsoLogin - Is SSO login flag
   */
  setIsSsoLoginFlag(isSsoLogin) {
    this.#isSsoLogin = isSsoLogin;
  }

  /**
   * Checks if the tracking service has been properly initialized.
   * @returns {boolean} True if initialized and ready, false otherwise
   */
  isInitialized() {
    return this.#initialized;
  }

  /**
   * Checks if xAPI tracking is available and configured.
   * @returns {boolean} True if xAPI is available, false otherwise
   */
  isXAPIAvailable() {
    return this.#enabled && this.#apiEndpoint;
  }

  /**
   * Checks if xAPI tracking is enabled.
   * @returns {boolean} True if xAPI tracking is enabled, false otherwise
   */
  isXAPIEnabled() {
    return this.#enabled;
  }

  /**
   * Validates event data before sending
   * @private
   * @param {Object} eventData - Event data to validate
   * @returns {boolean} True if valid, false otherwise
   */
  #validateEventData(eventData) {
    const requiredFields = [
      "event_type",
      "user_id",
      "object_id",
      "object_name",
      "object_type",
    ];

    for (const field of requiredFields) {
      if (!eventData[field]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Recursively converts object keys to snake_case
   * @private
   * @param {Object} data - The object to convert
   * @returns {Object} New object with snake_case keys
   */
  #toSnakeCase(data) {
    if (data === null || typeof data !== "object") {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.#toSnakeCase(item));
    }

    return Object.keys(data).reduce((acc, key) => {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(
        /[A-Z]/g,
        (letter) => `_${letter.toLowerCase()}`
      );
      acc[snakeKey] = this.#toSnakeCase(data[key]);
      return acc;
    }, {});
  }

  /**
   * The reportable half of an event's results. The LRS records success and
   * completed; any other key an event carries is dropped rather than sent.
   *
   * @private
   * @param {Object} [results]
   * @returns {{ results?: Object }} Empty when neither is reported, so the key
   *   drops out of the payload rather than riding as an empty object.
   */
  #reportedResults(results) {
    const reported = {};

    if (results?.success !== undefined) {
      reported.success = results.success;
    }

    if (results?.completed !== undefined) {
      reported.completed = results.completed;
    }

    return Object.keys(reported).length > 0 ? { results: reported } : {};
  }

  /**
   * Builds event data object from event class or parameters
   *
   * Extensions ride flat beside the core fields: the xAPI service matches each
   * payload property to its extension IRI by name, so an event's options are
   * spread in whole rather than nested under an extensions key.
   *
   * @private
   * @param {string|Object} eventTypeOrClass - Event type string or event class instance
   * @param {Object} objectInfo - Object information (ignored if first param is event class)
   * @param {Object} options - Additional options (ignored if first param is event class)
   * @returns {Object|null} Complete event data object or null if invalid
   */
  #buildEventData(eventTypeOrClass, objectInfo, options = {}) {
    let eventType, _objectInfo, _options;

    if (typeof eventTypeOrClass === "object" && eventTypeOrClass.getEventData) {
      const eventData = eventTypeOrClass.getEventData();
      eventType = eventData.eventType;
      _objectInfo = eventData.objectInfo;
      _options = eventData.options;
    } else {
      eventType = eventTypeOrClass;
      _objectInfo = objectInfo;
      _options = options;
    }

    const deviceInfo = getDeviceInfo();
    const { results, ...eventOptions } = _options;

    const rawEventData = {
      eventType: eventType,
      userId: this.#userId,
      sessionId: this.#sessionId,
      timestamp: new Date().toISOString(),
      objectId: _objectInfo.id,
      objectName: _objectInfo.name,
      objectType: _objectInfo.type,
      userRoleId: this.#userRoleId,
      ...deviceInfo,
      organizationId: this.#organizationId,
      classroomId: _options.classroomId || this.#classroomId,
      productId: _options.productId || this.#productId,
      applicationName: XAPI_CONFIG.APPLICATION_NAME,
      targetType: _options.targetType || null,
      targetId: _options.targetId || null,
      authenticationMethod: this.#isSsoLogin
        ? XAPI_CONFIG.SSO_AUTHENTICATION_METHOD
        : XAPI_CONFIG.USERNAME_AUTHENTICATION_METHOD,
      ...eventOptions,
      ...this.#reportedResults(results),
    };

    const finalEventData = this.#toSnakeCase(rawEventData);

    if (!this.#validateEventData(finalEventData)) {
      return null;
    }

    return finalEventData;
  }

  /**
   * Sends an xAPI event to the API endpoint.
   * @private
   * @param {Object} eventData - The xAPI event data
   */
  async #sendEvent(eventData) {
    if (this.#enabled && !this.#apiEndpoint) {
      this.#enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (this.#token) {
        headers["Authorization"] = `Bearer ${this.#token}`;
      }

      const response = await fetch(this.#apiEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        console.error(
          `Failed to send xAPI event (${eventData.event_type}):`,
          response.status,
          response.statusText,
          eventData
        );
      }
    } catch (error) {
      console.error(
        `Error sending xAPI event (${eventData.event_type}):`,
        error,
        eventData
      );
    }
  }

  /**
   * Sends multiple xAPI events as a batch to the API endpoint.
   * @private
   * @param {Array} eventsData - Array of xAPI event data objects
   */
  async #sendBatchEvents(eventsData) {
    if (this.#enabled && !this.#apiEndpoint) {
      this.#enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
    }

    try {
      const batchEndpoint = this.#apiEndpoint + "batch/";
      const headers = {
        "Content-Type": "application/json",
      };

      if (this.#token) {
        headers["Authorization"] = `Bearer ${this.#token}`;
      }

      const response = await fetch(batchEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(eventsData),
      });

      if (!response.ok) {
        console.error(
          "Failed to send xAPI batch events:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error sending xAPI batch events:", error);
    }
  }

  /**
   * Tracks an xAPI learning event based on the CSV specification.
   * @param {string|Object} eventTypeOrClass - The type of event (authenticated, viewed, completed, etc.) or an xAPI event class instance
   * @param {Object} objectInfo - Information about the learning object (ignored if first param is event class)
   * @param {Object} options - Additional options (ignored if first param is event class)
   */
  async trackEvent(eventTypeOrClass, objectInfo, options = {}) {
    if (!this.isXAPIAvailable()) {
      return;
    }

    const eventData = this.#buildEventData(
      eventTypeOrClass,
      objectInfo,
      options
    );
    if (eventData) {
      await this.#sendEvent(eventData);
    }
  }

  /**
   * Tracks multiple xAPI events, automatically choosing between batch and individual calls.
   * @param {Array} events - Array of event class instances or event data objects
   * @param {boolean} useBatch - Whether to use batch API when multiple events (default: true)
   */
  async trackEvents(events, useBatch = true) {
    if (!this.isXAPIAvailable() || !events || events.length === 0) {
      return;
    }

    const validEventsData = events
      .map((event) => this.#buildEventData(event))
      .filter((eventData) => eventData !== null);

    if (validEventsData.length === 0) {
      return;
    }

    if (useBatch && validEventsData.length > 1) {
      await this.#sendBatchEvents(validEventsData);
    } else {
      const eventPromises = validEventsData.map((eventData) =>
        this.#sendEvent(eventData)
      );
      await Promise.allSettled(eventPromises);
    }
  }
}

export default xAPITrackingService;
